import { useState, useEffect, useCallback } from "react";

/* ─── BRAND TOKENS ─────────────────────────────────────────────────────── */
const T = {
  cyan:    "#00C8FF",
  black:   "#080808",
  white:   "#FFFFFF",
  grey:    "#777777",
  surface: "#1A1A1A",
  border:  "#2A2A2A",
  red:     "#FF3B3B",
};

const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;700;800;900&family=Barlow:wght@400;500&display=swap";

/* ─── CONSTANTS ────────────────────────────────────────────────────────── */
const ADMIN_PASS = "2912";
const AUTH_KEY   = "pc_admin_auth";

const ATTRIBUTES = [
  { key: "score_aerobic",   label: "AEROBIC BASE",          icon: "🏃" },
  { key: "score_speed",     label: "SPEED & PACE",           icon: "⚡" },
  { key: "score_strength",  label: "STRENGTH & POWER",       icon: "💪" },
  { key: "score_recovery",  label: "RECOVERY & RESILIENCE",  icon: "🔄" },
  { key: "score_nutrition", label: "NUTRITION & FUELLING",   icon: "🥗" },
  { key: "score_mental",    label: "MENTAL TOUGHNESS",       icon: "🧠" },
  { key: "score_readiness", label: "RACE READINESS",         icon: "🎯" },
];

/* ─── HELPERS ──────────────────────────────────────────────────────────── */
function scoreColor(s) {
  if (s >= 75) return T.cyan;
  if (s >= 50) return T.white;
  if (s >= 25) return T.grey;
  return T.red;
}

function scoreLabel(s) {
  if (s >= 75) return "STRONG";
  if (s >= 50) return "DEVELOPING";
  if (s >= 25) return "NEEDS WORK";
  return "CRITICAL GAP";
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day:   "2-digit",
    month: "short",
    year:  "numeric",
  });
}

/* ─── FONT STYLES ──────────────────────────────────────────────────────── */
const fCondensed = (size, weight = 700) => ({
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: weight,
  fontSize:   size,
  textTransform: "uppercase",
  letterSpacing: "0.15em",
});
const fBody = (size = "14px") => ({
  fontFamily: "'Barlow', sans-serif",
  fontWeight: 400,
  fontSize:   size,
  lineHeight: "165%",
});
const fLabel = {
  fontFamily: "'Barlow Condensed', sans-serif",
  fontWeight: 700,
  fontSize:   "10px",
  textTransform: "uppercase",
  letterSpacing: "0.25em",
};

/* ═══════════════════════════════════════════════════════════════════════
   LOGIN SCREEN
══════════════════════════════════════════════════════════════════════ */
function LoginScreen({ onAuth }) {
  const [pw,    setPw]    = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  function attempt() {
    if (pw === ADMIN_PASS) {
      localStorage.setItem(AUTH_KEY, "1");
      onAuth();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: T.black,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Barlow', sans-serif",
    }}>
      <link rel="stylesheet" href={FONT_URL} />

      <div style={{
        width: "100%",
        maxWidth: 360,
        padding: "0 24px",
        textAlign: "center",
        animation: "fadeUp .45s ease",
      }}>
        {/* Logo */}
        <img
          src="/power-coaching-logo.png"
          alt="Power Coaching"
          style={{ width: 140, height: "auto", display: "block", margin: "0 auto 36px" }}
        />

        {/* Title */}
        <div style={{ marginBottom: 36 }}>
          <p style={{ ...fLabel, color: T.grey, marginBottom: 10 }}>ADMIN ACCESS</p>
          <h1 style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 900,
            fontSize: 44,
            textTransform: "uppercase",
            lineHeight: 0.92,
            color: T.white,
          }}>
            POWER<br /><span style={{ color: T.cyan }}>COACHING</span>
          </h1>
        </div>

        {/* Form */}
        <div style={{ animation: shake ? "shake .42s ease" : "none" }}>
          <input
            type="password"
            placeholder="PASSWORD"
            value={pw}
            autoFocus
            onChange={e => { setPw(e.target.value); setError(false); }}
            onKeyDown={e => e.key === "Enter" && attempt()}
            style={{
              width: "100%",
              background: T.surface,
              border: `1px solid ${error ? T.red : T.border}`,
              color: T.white,
              padding: "14px 16px",
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: 20,
              letterSpacing: "0.4em",
              textAlign: "center",
              outline: "none",
              boxSizing: "border-box",
              marginBottom: 6,
              transition: "border-color 0.2s",
            }}
          />

          {error && (
            <p style={{ ...fLabel, color: T.red, marginBottom: 10 }}>
              INCORRECT PASSWORD
            </p>
          )}

          <button
            onClick={attempt}
            style={{
              width: "100%",
              background: T.cyan,
              color: T.black,
              border: "none",
              padding: "14px 32px",
              cursor: "pointer",
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              marginTop: 6,
              transition: "opacity 0.15s",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >
            ENTER →
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-9px)} 40%{transform:translateX(9px)} 60%{transform:translateX(-6px)} 80%{transform:translateX(6px)} }
        *{box-sizing:border-box;margin:0;padding:0;}
        input::placeholder{color:#333;letter-spacing:0.15em;}
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ATTRIBUTE BAR (inside expanded row)
══════════════════════════════════════════════════════════════════════ */
function AttrBar({ attr, value, idx }) {
  const c   = scoreColor(value ?? 0);
  const lbl = scoreLabel(value ?? 0);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 6,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ fontSize: 14 }}>{attr.icon}</span>
          <span style={{ ...fCondensed("11px"), color: T.white }}>{attr.label}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ ...fCondensed("10px"), color: c }}>{lbl}</span>
          <span style={{ fontFamily: "'Barlow Condensed'", fontWeight: 900, fontSize: 22, color: c, lineHeight: 1 }}>{value ?? 0}</span>
        </div>
      </div>
      <div style={{ height: 2, background: T.border }}>
        <div style={{
          height: "100%",
          background: c,
          width: `${value ?? 0}%`,
          transition: `width 0.75s ease ${idx * 0.07}s`,
        }} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   EXPANDED ROW DETAIL
══════════════════════════════════════════════════════════════════════ */
function SubmissionDetail({ row }) {
  const summaryParas = row.summary
    ? (Array.isArray(row.summary) ? row.summary : row.summary.split("\n\n").filter(Boolean))
    : [];

  const actionPoints = Array.isArray(row.action_points) && row.action_points.length > 0
    ? row.action_points
    : null;

  return (
    <div style={{
      padding: "24px 28px 28px",
      background: "#0b0b0b",
      borderTop: `1px solid ${T.border}`,
      animation: "fadeDown .25s ease",
    }}>

      {/* Headline */}
      <h3 style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        fontWeight: 900,
        fontSize: 26,
        textTransform: "uppercase",
        letterSpacing: "-0.3px",
        lineHeight: 1,
        marginBottom: 20,
      }}>
        {row.headline
          ? row.headline.split(" ").map((w, i) => (
              <span key={i} style={{ color: i === 0 ? T.cyan : T.white }}>{w}{" "}</span>
            ))
          : <span style={{ color: T.grey }}>No headline stored</span>
        }
      </h3>

      {/* Warning */}
      {row.warning && (
        <div style={{
          background: "#0d0600",
          border: "1px solid #3a1a00",
          padding: "10px 14px",
          marginBottom: 20,
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
        }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
          <p style={{ ...fBody("13px"), color: "#ffaa55", margin: 0 }}>{row.warning}</p>
        </div>
      )}

      {/* Attribute bars */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ ...fLabel, color: T.grey, marginBottom: 14 }}>ATTRIBUTE BREAKDOWN</p>
        {ATTRIBUTES.map((attr, i) => (
          <AttrBar key={attr.key} attr={attr} value={row[attr.key]} idx={i} />
        ))}
      </div>

      {/* Summary */}
      {summaryParas.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 20, marginBottom: actionPoints ? 24 : 0 }}>
          <p style={{ ...fLabel, color: T.grey, marginBottom: 14 }}>COACHING ANALYSIS · SAM</p>
          {summaryParas.map((para, i) => (
            <p key={i} style={{ ...fBody("14px"), color: "#999", marginBottom: 14 }}>{para}</p>
          ))}
        </div>
      )}

      {/* Action Points */}
      {actionPoints && (
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 20 }}>
          <p style={{ ...fLabel, color: T.grey, marginBottom: 14 }}>YOUR ACTION POINTS</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {actionPoints.map((point, i) => {
              const dot   = point.indexOf(".");
              const first = dot !== -1 ? point.slice(0, dot + 1) : point;
              const rest  = dot !== -1 ? point.slice(dot + 1).trim() : "";
              return (
                <div key={i} style={{
                  background: "#1A1A1A",
                  border: "1px solid #2A2A2A",
                  padding: "16px",
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}>
                  <span style={{ fontFamily: "'Barlow Condensed'", fontWeight: 900, fontSize: 22, color: "#00C8FF", lineHeight: 1, flexShrink: 0, minWidth: 30 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p style={{ ...fBody("14px"), margin: 0 }}>
                    <span style={{ color: "#00C8FF" }}>{first}</span>
                    {rest && <span style={{ color: T.white }}>{" "}{rest}</span>}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   TABLE ROW
══════════════════════════════════════════════════════════════════════ */
const COL_TEMPLATE = "140px 130px 1fr 110px 170px 90px";

function SubmissionRow({ row, expanded, onToggle, zebra }) {
  const score = row.overall_score ?? 0;
  const c     = scoreColor(score);
  const base  = expanded ? "#101010" : zebra ? "#050505" : T.black;

  return (
    <div>
      {/* Clickable row */}
      <div
        onClick={onToggle}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && onToggle()}
        style={{
          display: "grid",
          gridTemplateColumns: COL_TEMPLATE,
          alignItems: "center",
          gap: 0,
          padding: "13px 20px",
          borderBottom: `1px solid ${T.border}`,
          cursor: "pointer",
          background: base,
          transition: "background 0.12s",
          userSelect: "none",
        }}
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = "#0e0e0e"; }}
        onMouseLeave={e => { e.currentTarget.style.background = base; }}
      >
        <span style={{ ...fBody("12px"), color: T.grey }}>{formatDate(row.created_at)}</span>
        <span style={{ ...fCondensed("14px", 700), color: T.white, letterSpacing: "0.08em" }}>{row.first_name || "—"}</span>
        <span style={{ ...fBody("12px"), color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12 }}>{row.email || "—"}</span>
        <span style={{ ...fCondensed("13px", 700), color: T.white, letterSpacing: "0.08em" }}>{row.race_distance || "—"}</span>
        <span style={{ ...fBody("12px"), color: T.grey, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12 }}>{row.race_goal || "—"}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "'Barlow Condensed'", fontWeight: 900, fontSize: 24, color: c, lineHeight: 1 }}>{score}</span>
          <span style={{
            fontSize: 10,
            color: T.grey,
            display: "inline-block",
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            lineHeight: 1,
          }}>▶</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && <SubmissionDetail row={row} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════════════════════════════════ */
function Dashboard({ onSignOut }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [fetchError,  setFetchError]  = useState(null);
  const [search,      setSearch]      = useState("");
  const [expandedId,  setExpandedId]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch("/api/get-submissions");
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSubmissions(data || []);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = submissions.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (s.first_name || "").toLowerCase().includes(q) ||
      (s.email      || "").toLowerCase().includes(q)
    );
  });

  function toggleRow(id) {
    setExpandedId(prev => (prev === id ? null : id));
  }

  return (
    <div style={{ minHeight: "100vh", background: T.black, color: T.white, fontFamily: "'Barlow', sans-serif" }}>
      <link rel="stylesheet" href={FONT_URL} />

      {/* ── STICKY HEADER ── */}
      <div style={{
        background: "#0a0a0a",
        borderBottom: `1px solid ${T.border}`,
        padding: "0 28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: 60,
        position: "sticky",
        top: 0,
        zIndex: 200,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          <img
            src="/power-coaching-logo.png"
            alt="Power Coaching"
            style={{ height: 34, width: "auto" }}
          />
          <div style={{ width: 1, height: 26, background: T.border }} />
          <p style={{ ...fLabel, color: T.grey }}>DASHBOARD</p>
        </div>

        <button
          onClick={onSignOut}
          style={{
            background: "transparent",
            color: T.grey,
            border: `1px solid ${T.border}`,
            padding: "7px 16px",
            cursor: "pointer",
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.grey; e.currentTarget.style.color = T.white; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.grey; }}
        >
          SIGN OUT
        </button>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: 1140, margin: "0 auto", padding: "32px 24px 60px" }}>

        {/* Stats + search bar */}
        <div style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 28,
          flexWrap: "wrap",
        }}>
          <div>
            <p style={{ ...fLabel, color: T.grey, marginBottom: 6 }}>TOTAL SUBMISSIONS</p>
            <p style={{ fontFamily: "'Barlow Condensed'", fontWeight: 900, fontSize: 48, color: T.cyan, lineHeight: 1 }}>
              {loading ? "—" : submissions.length}
            </p>
          </div>

          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="SEARCH NAME OR EMAIL..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                color: T.white,
                padding: "10px 14px",
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                outline: "none",
                width: 300,
                transition: "border-color 0.2s",
              }}
              onFocus={e => (e.target.style.borderColor = `${T.cyan}55`)}
              onBlur={e  => (e.target.style.borderColor = T.border)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  color: T.grey,
                  cursor: "pointer",
                  fontSize: 14,
                  lineHeight: 1,
                  padding: 0,
                }}
              >✕</button>
            )}
          </div>
        </div>

        {/* Error banner */}
        {fetchError && (
          <div style={{
            background: "#100000",
            border: "1px solid #330000",
            padding: "12px 16px",
            marginBottom: 20,
            ...fBody("13px"),
            color: "#ff6666",
          }}>
            Error loading submissions: {fetchError} —{" "}
            <button
              onClick={load}
              style={{ background: "none", border: "none", color: T.cyan, cursor: "pointer", textDecoration: "underline", fontSize: "inherit", fontFamily: "inherit" }}
            >
              retry
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: "70px 0" }}>
            <div style={{
              width: 32,
              height: 32,
              border: `2px solid ${T.surface}`,
              borderTop: `2px solid ${T.cyan}`,
              borderRadius: "50%",
              margin: "0 auto 16px",
              animation: "spin .7s linear infinite",
            }} />
            <p style={{ ...fLabel, color: T.grey }}>LOADING SUBMISSIONS...</p>
          </div>
        )}

        {/* Table */}
        {!loading && !fetchError && (
          <div style={{ border: `1px solid ${T.border}`, overflow: "hidden" }}>

            {/* Column headers */}
            <div style={{
              display: "grid",
              gridTemplateColumns: COL_TEMPLATE,
              alignItems: "center",
              background: T.surface,
              borderBottom: `1px solid ${T.border}`,
              padding: "10px 20px",
            }}>
              {["DATE", "NAME", "EMAIL", "DISTANCE", "GOAL", "SCORE"].map(h => (
                <p key={h} style={{ ...fLabel, color: T.grey, margin: 0 }}>{h}</p>
              ))}
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div style={{ padding: "48px 20px", textAlign: "center" }}>
                <p style={{ ...fLabel, color: "#333" }}>
                  {search.trim() ? "NO RESULTS MATCH YOUR SEARCH" : "NO SUBMISSIONS YET"}
                </p>
              </div>
            )}

            {/* Rows */}
            {filtered.map((row, idx) => (
              <SubmissionRow
                key={row.id}
                row={row}
                expanded={expandedId === row.id}
                onToggle={() => toggleRow(row.id)}
                zebra={idx % 2 !== 0}
              />
            ))}
          </div>
        )}

        {/* Result count */}
        {!loading && filtered.length > 0 && (
          <p style={{ ...fLabel, color: "#2a2a2a", marginTop: 14, textAlign: "right" }}>
            {search.trim()
              ? `${filtered.length} RESULT${filtered.length !== 1 ? "S" : ""} OF ${submissions.length}`
              : `${submissions.length} SUBMISSION${submissions.length !== 1 ? "S" : ""}`
            }
          </p>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeDown { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        * { box-sizing:border-box; margin:0; padding:0; }
        input::placeholder { color:#333; letter-spacing:0.1em; }
        button { border-radius:0 !important; }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ROOT — handles auth gate
══════════════════════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const [authed, setAuthed] = useState(() => localStorage.getItem(AUTH_KEY) === "1");

  function signOut() {
    localStorage.removeItem(AUTH_KEY);
    setAuthed(false);
  }

  if (!authed) return <LoginScreen onAuth={() => setAuthed(true)} />;
  return <Dashboard onSignOut={signOut} />;
}
